import { useCallback, useEffect, useState } from 'react';
import { Card, Dropdown, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Scrollbar from 'components/base/Scrollbar';
import Button from 'components/base/Button';
import NotificationItem from 'components/common/NotificationItem';
import classNames from 'classnames';
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import {
  getNotifications,
  markAllNotificationsAsRead,
  NotificationResponse,
  Notification
} from 'services/api/notification';
import toast from 'react-hot-toast';
import LoadingPlaceholder from 'components/loading-placeholder/LoadingPlaceholder';
import { useNotificationSocket } from 'hooks/useNotificationSocket';
import { useAccessToken } from 'providers/AccessTokenProvider';

const NOTIFICATIONS_PER_PAGE = 10;

const prependNotificationToPage = (page: any, notification: Notification) => {
  // Case 1: page is already an array of Notification
  if (Array.isArray(page)) {
    return [notification, ...page];
  }

  // Case 2: page is NotificationResponse { data: Notification[] }
  if (page?.data && Array.isArray(page.data)) {
    return {
      ...page,
      data: [notification, ...page.data],
      total: (page.total || 0) + 1
    };
  }

  // Case 3: page is AxiosResponse<NotificationResponse> { data: { data: Notification[] } }
  if (page?.data?.data && Array.isArray(page.data.data)) {
    return {
      ...page,
      data: {
        ...page.data,
        data: [notification, ...page.data.data],
        total: (page.data.total || 0) + 1
      }
    };
  }

  console.error('Unexpected notifications page structure (prepend):', page);
  return page;
};

const setReadOnPage = (page: any, notificationId: string, read: boolean) => {
  if (Array.isArray(page)) {
    return page.map((n: Notification) =>
      n.id === notificationId ? { ...n, read } : n
    );
  }

  if (page?.data && Array.isArray(page.data)) {
    return {
      ...page,
      data: page.data.map((n: Notification) =>
        n.id === notificationId ? { ...n, read } : n
      )
    };
  }

  if (page?.data?.data && Array.isArray(page.data.data)) {
    return {
      ...page,
      data: {
        ...page.data,
        data: page.data.data.map((n: Notification) =>
          n.id === notificationId ? { ...n, read } : n
        )
      }
    };
  }

  console.error('Unexpected notifications page structure (setRead):', page);
  return page;
};

const markAllReadOnPage = (page: any) => {
  if (Array.isArray(page)) {
    return page.map((n: Notification) => ({ ...n, read: true }));
  }

  if (page?.data && Array.isArray(page.data)) {
    return {
      ...page,
      data: page.data.map((n: Notification) => ({ ...n, read: true }))
    };
  }

  if (page?.data?.data && Array.isArray(page.data.data)) {
    return {
      ...page,
      data: {
        ...page.data,
        data: page.data.data.map((n: Notification) => ({ ...n, read: true }))
      }
    };
  }

  console.error('Unexpected notifications page structure (allRead):', page);
  return page;
};

const NotificationDropdownMenu = ({ className }: { className?: string }) => {
  const queryClient = useQueryClient();
  const { authUser } = useAccessToken();
  const [isMenuMounted, setIsMenuMounted] = useState<boolean>(false);

  const {
    data: notificationsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useInfiniteQuery(
    ['notifications'],
    ({ pageParam = 1 }) =>
      getNotifications({ page: pageParam, limit: NOTIFICATIONS_PER_PAGE }),
    {
      getNextPageParam: lastPage => {
        const response = lastPage.data as NotificationResponse;
        return response.page < Math.ceil(response.total / response.limit)
          ? response.page + 1
          : undefined;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      // Always refetch when the bell is opened so the list can't get stuck on a
      // stale/empty cache (e.g. after a transient backend error); the socket
      // keeps it live while open.
      refetchOnMount: 'always',
      select: data => ({
        pages: data.pages.map(page => page.data),
        pageParams: data.pageParams
      }),
      enabled: isMenuMounted
    }
  );

  useEffect(() => {
    setIsMenuMounted(true);

    return () => setIsMenuMounted(false);
  }, []);

  const markAllReadMutation = useMutation(markAllNotificationsAsRead, {
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['notificationCount']);
      toast.success('All notifications marked as read');
    }
  });

  const handleMarkAllAsRead = () => {
    markAllReadMutation.mutate();
  };

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const isAtBottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 100;

      if (isAtBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const notifications =
    notificationsPages?.pages.flatMap(page => page.data) || [];

  useNotificationSocket({
    enabled: Boolean(authUser),
    onNewNotification: (notification: Notification) => {
      if (!isMenuMounted) return;

      queryClient.setQueryData(['notifications'], (old: any) => {
        if (!old || !old.pages || old.pages.length === 0) return old;

        const firstPage = old.pages[0];

        if (!firstPage) return old;

        const updatedFirstPage = prependNotificationToPage(
          firstPage,
          notification
        );

        return {
          ...old,
          pages: [updatedFirstPage, ...old.pages.slice(1)]
        };
      });

      // Only invalidate notifications list to refresh, count is handled via socket
      queryClient.invalidateQueries(['notifications']);
    },
    onNotificationCountUpdate: data => {
      // Update count directly without invalidation - socket provides the latest value
      queryClient.setQueryData(['notificationCount'], () => ({
        data: { count: data.count }
      }));
    },
    onNotificationRead: data => {
      if (!isMenuMounted) return;
      queryClient.setQueryData(['notifications'], (old: any) => {
        if (!old || !old.pages) return old;

        const updatedPages = old.pages.map((page: any) =>
          setReadOnPage(page, data.notificationId, true)
        );

        return {
          ...old,
          pages: updatedPages
        };
      });

      // Only invalidate notifications list, count is handled via socket
      queryClient.invalidateQueries(['notifications']);
    },
    onAllNotificationsRead: () => {
      if (!isMenuMounted) return;
      queryClient.setQueryData(['notifications'], (old: any) => {
        if (!old || !old.pages) return old;

        const updatedPages = old.pages.map((page: any) =>
          markAllReadOnPage(page)
        );

        return {
          ...old,
          pages: updatedPages
        };
      });

      // Only invalidate notifications list, count is handled via socket
      queryClient.invalidateQueries(['notifications']);
    }
  });

  return (
    <Dropdown.Menu
      align="end"
      className={classNames(
        className,
        'navbar-dropdown-caret py-0 notification-dropdown-menu shadow border border-300'
      )}
    >
      <Card className="position-relative border-0">
        <Card.Header className="p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="text-black mb-0">Notifications</h5>
            <Button
              variant="link"
              className="p-0 fs-9 fw-normal"
              onClick={handleMarkAllAsRead}
              disabled={markAllReadMutation.isLoading}
            >
              Mark all as read
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0" style={{ height: '27rem' }}>
          <Scrollbar onScroll={handleScroll}>
            {isLoading ? (
              <LoadingPlaceholder />
            ) : notifications.length === 0 ? (
              <div className="p-3 text-center">No notifications</div>
            ) : (
              notifications.map((notification, index) => (
                <NotificationItem
                  notification={notification}
                  type="dropdownItem"
                  className={classNames({
                    'border-bottom': index !== notifications.length - 1
                  })}
                  key={`${notification.id}-${notification.read}-${notification.archived}`}
                />
              ))
            )}
            {isFetchingNextPage && (
              <Spinner
                animation="border"
                variant="primary"
                className="position-absolute top-50 start-50 translate-middle"
              />
            )}
          </Scrollbar>
        </Card.Body>
        <Card.Footer className="p-0 border-top">
          <div className="my-2 text-center fw-bold fs-10 text-600">
            <Link to="/notifications" className="fw-bolder">
              Notification history
            </Link>
          </div>
        </Card.Footer>
      </Card>
    </Dropdown.Menu>
  );
};

export default NotificationDropdownMenu;
